import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import QRCode from "qrcode";
import { FaDownload, FaExclamationCircle, FaCheckCircle, FaTimes, FaSync, FaWallet, FaCreditCard } from "react-icons/fa";

const InfluenerGetIdCard = () => {
  const navigate = useNavigate();
  const [idCardData, setIdCardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showIdCard, setShowIdCard] = useState(false);
  const [message, setMessage] = useState("");
  const [rejectedNote, setRejectedNote] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [isFlipped, setIsFlipped] = useState(false);
  const [userRole, setUserRole] = useState("Influencer");

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (user.role) {
      setUserRole(user.role);
    }
  }, []);

  useEffect(() => {
    if (idCardData?.data?._id) {
      const qrValue = `${process.env.REACT_APP_API_BASE_URL || 'https://iinsaf.com'}/influencer/view/${idCardData.data._id}`;
      QRCode.toDataURL(qrValue, {
        width: 300,
        margin: 1,
        color: { dark: '#000000', light: '#ffffff' }
      }).then(url => setQrDataUrl(url)).catch(console.error);
    }
  }, [idCardData]);

  const handleGetIdCard = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/influencer/get/icard`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.status === 201) {
        setIdCardData(response.data);
        setShowIdCard(true);
      } else if (response.status === 200) {
        if (response.data.message) {
          setMessage(response.data.message);
          setRejectedNote(response.data.rejectedNote || null);
        }
      } else {
        toast.error("You are not eligible for this ID card");
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setMessage("You are not verified yet. Please create your ID card");
        toast.info("You are not verified yet. Please create your ID card");
      } else {
        toast.error("Failed to fetch ID card. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const downloadIdCards = async () => {
    const { toPng } = await import("html-to-image");
    const jsPDF = (await import("jspdf")).jsPDF;

    const front = document.getElementById("card-front");
    const back = document.getElementById("card-back");

    if (!front || !back) {
      toast.error("Card elements not found.");
      return;
    }

    toast.info("Generating PDF...", { autoClose: 3000 });
    await document.fonts.ready;

    try {
      const captureOptions = {
        quality: 1.0,
        pixelRatio: 4, // Higher resolution for professional print
        backgroundColor: "#ffffff",
        cacheBust: true,
        style: { transform: 'scale(1)', transformOrigin: 'top left', margin: 0 },
        width: 400,
        height: 770,
      };

      const originalFrontDisplay = front.parentElement.style.display;
      const originalBackDisplay = back.parentElement.style.display;
      front.parentElement.style.display = 'block';
      back.parentElement.style.display = 'block';

      const frontPng = await toPng(front, captureOptions);
      const backPng = await toPng(back, captureOptions);

      front.parentElement.style.display = originalFrontDisplay;
      back.parentElement.style.display = originalBackDisplay;

      const pdf = new jsPDF("portrait", "mm", [82, 158]);
      pdf.addImage(frontPng, "PNG", 0, 0, 82, 158);
      pdf.addPage([82, 158], "portrait");
      pdf.addImage(backPng, "PNG", 0, 0, 82, 158);

      pdf.save("INFLUENCER_ID_Card.pdf");
      toast.success("ID Cards downloaded successfully!");
    } catch (err) {
      console.error("PDF generation error:", err);
      toast.error("Failed to generate PDF.");
    }
  };

  const handleNavigateToCards = () => {
    setMessage("");
    setRejectedNote(null);
    navigate("/influencer/id-cards");
  };

  useEffect(() => {
    handleGetIdCard();
    // eslint-disable-next-line
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <div className="text-center animate-pulse">
          <FaSync className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <h2 className="text-lg font-black text-slate-900 uppercase tracking-widest">Loading Credentials</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-indigo-50">
      {message && (
        <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="bg-white/95 backdrop-blur-xl rounded-[2.5rem] shadow-2xl max-w-[360px] w-full border border-white/40 overflow-hidden transform animate-scaleUp">
            <div className={`p-1.5 ${rejectedNote ? 'bg-rose-500' : 'bg-indigo-600'}`}>
              <div className="flex items-center justify-between px-4 py-2">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-white/20 rounded-lg flex items-center justify-center">
                    {rejectedNote ? <FaExclamationCircle className="w-3 h-3 text-white" /> : <FaCheckCircle className="w-3 h-3 text-white" />}
                  </div>
                  <span className="text-[10px] font-black text-white uppercase tracking-widest">{rejectedNote ? "Review Notification" : "System Message"}</span>
                </div>
                <button onClick={handleNavigateToCards} className="p-1 hover:bg-white/20 rounded-lg"><FaTimes className="w-3 h-3 text-white" /></button>
              </div>
            </div>
            <div className="p-8 text-center">
              <div className="relative inline-block mb-6">
                <div className={`w-20 h-20 rounded-[2rem] flex items-center justify-center mx-auto ${rejectedNote ? 'bg-rose-50 text-rose-500 shadow-rose-100' : 'bg-indigo-50 text-indigo-600 shadow-indigo-100'} shadow-xl border-4 border-white`}>
                  {rejectedNote ? <FaExclamationCircle className="w-8 h-8" /> : <div className="relative"><FaSync className="w-8 h-8 animate-spin-slow opacity-20 absolute inset-0" /><FaWallet className="w-8 h-8 relative z-10" /></div>}
                </div>
              </div>
              <h3 className={`text-xl font-black mb-3 tracking-tight ${rejectedNote ? 'text-rose-600' : 'text-slate-900'}`}>{rejectedNote ? 'Action Required' : 'Under Review'}</h3>
              <p className="text-slate-500 text-xs font-bold leading-relaxed mb-6 px-4">{message}</p>
              {rejectedNote && (
                <div className="bg-rose-50/50 border border-rose-100 rounded-2xl p-4 mb-8 text-left">
                  <h4 className="text-[10px] font-black text-rose-600 uppercase tracking-widest mb-1.5 flex items-center gap-1.5"><FaExclamationCircle className="w-3 h-3" /> Audit Feedback</h4>
                  <p className="text-xs font-bold text-rose-700 leading-relaxed">{rejectedNote}</p>
                </div>
              )}
              <button onClick={handleNavigateToCards} className={`w-full py-3.5 rounded-2xl text-white font-black text-xs uppercase tracking-widest transition-all shadow-lg ${rejectedNote ? 'bg-rose-500' : 'bg-slate-900'}`}>{rejectedNote ? 'Update Application' : 'Okay, Got it'}</button>
            </div>
          </div>
        </div>
      )}

      {showIdCard && (
        <>
          <div className="bg-white/95 backdrop-blur-sm border-b border-gray-100 sticky top-0 z-30">
            <div className="max-w-7xl mx-auto px-4 py-3 sm:py-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3 text-center sm:text-left">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                    <FaCheckCircle className="text-white w-6 h-6" />
                  </div>
                  <div className="text-left">
                    <h1 className="text-xl sm:text-2xl font-black text-slate-900 leading-tight">Official <span className="text-blue-600">ID Credentials</span></h1>
                    <p className="text-[10px] sm:text-xs text-slate-500 font-bold uppercase tracking-widest">Verified Influencer Identity</p>
                  </div>
                </div>

                <button
                  onClick={downloadIdCards}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-blue-600 text-white font-black py-3 px-8 rounded-xl shadow-xl transition-all active:scale-95 text-sm uppercase tracking-widest"
                >
                  <FaDownload className="w-4 h-4" />
                  Download Official Card
                </button>
              </div>

              {/* Mobile Flip Toggle Button */}
              <div className="mt-6 flex justify-center xl:hidden">
                <div className="bg-slate-100 p-1 rounded-xl flex items-center shadow-inner relative w-64 max-w-full">
                  <div className={`absolute top-1 bottom-1 w-[calc(50%-4px)] bg-white rounded-lg shadow-sm transition-transform duration-300 ${isFlipped ? 'translate-x-full left-1' : 'translate-x-0 left-1'}`}></div>
                  <button
                    onClick={() => setIsFlipped(false)}
                    className={`flex-1 py-2.5 text-xs font-black uppercase tracking-widest relative z-10 transition-colors ${!isFlipped ? 'text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                    Front Side
                  </button>
                  <button
                    onClick={() => setIsFlipped(true)}
                    className={`flex-1 py-2.5 text-xs font-black uppercase tracking-widest relative z-10 transition-colors ${isFlipped ? 'text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                    Back Side
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 max-w-7xl mx-auto px-2 sm:px-4 py-4 sm:py-10 mb-20">
            <div id="id-cards-container" className="bg-white/50 backdrop-blur-sm rounded-[3rem] shadow-[0_25px_50px_-12px_rgba(0,0,0,0.1)] border border-white p-2 sm:p-10 flex flex-col items-center">
              {[idCardData].map((card, index) => (
                <div key={index} className="flex flex-col xl:flex-row justify-center items-center gap-0 sm:gap-6 xl:gap-12 w-full perspective-[1500px]">
                  {/* Card Front */}
                  <div className={`scale-[0.55] sm:scale-[0.65] md:scale-[0.70] xl:scale-[0.75] origin-top h-[430px] sm:h-[510px] md:h-[550px] xl:h-[580px] overflow-visible transition-all duration-500 ${isFlipped ? 'hidden xl:block' : 'block xl:block'}`}>
                    <div id="card-front" className="w-[400px] h-[770px] bg-white flex shadow-2xl rounded-2xl overflow-hidden border border-slate-100">
                      <div className="bg-red-600 w-[55px] flex items-center justify-center text-white font-bold relative pb-10">
                        <span className="rotate-90 whitespace-nowrap text-[32px] font-bold tracking-[0.6em] leading-none">RAISE YOUR VOICE</span>
                      </div>
                      <div className="bg-white p-4 relative flex-1 flex flex-col h-full">
                        <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
                          <img src="/images/iinsaf.png" className="w-full h-full object-contain" alt="" />
                        </div>
                        <div className="flex flex-col items-center mb-6 pt-2">
                          <img src="/images/iinsaf.png" className="h-16 mb-2" alt="Logo" />
                          <p className="text-[10px] font-black text-slate-800 uppercase text-center tracking-tighter leading-tight">
                            <span className="text-red-600">International Influencing</span><br />News Social Media Advertisement Federation
                          </p>
                        </div>
                        <div className="bg-gradient-to-r from-slate-900 to-indigo-950 text-white text-center py-3.5 rounded-xl mb-6 mx-5 shadow-xl border border-slate-700/50">
                          <p className="text-[10px] font-black tracking-[0.2em] text-indigo-300 uppercase mb-0.5">INFLUENCER ID</p>
                          <p className="text-2xl font-black font-mono tracking-tight">{card.data.iinsafId}</p>
                        </div>
                        <div className="flex justify-center mb-6">
                          <div className="w-32 h-42 rounded-2xl overflow-hidden border-[5px] border-white shadow-[0_8px_30px_rgba(0,0,0,0.12)]">
                            <img src={card.data.image || "/images/placeholder.png"} className="w-full h-full object-cover" alt="User" />
                          </div>
                        </div>
                        <div className="px-6 space-y-4 flex-1">
                          {[
                            { label: 'Name', value: card.data.name },
                            { label: 'Mobile', value: card.data.mobileNo },
                            { label: 'Blood Group', value: card.data.bloodGroup, color: 'text-red-600 font-extrabold' },
                            { label: 'Valid Thru', value: card.data.validUpto, color: 'text-emerald-600 font-extrabold' }
                          ].map((item, i) => (
                            <div key={i} className="flex justify-between items-end border-b border-slate-50 pb-1.5">
                              <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest">{item.label}</span>
                              <span className={`text-[15px] font-black text-slate-900 ${item.color || ''}`}>{item.value}</span>
                            </div>
                          ))}
                        </div>
                        <div className="p-8 text-right relative h-28 mt-auto">
                          <div className="relative inline-block w-40">
                            <img src="/assets/signature.png" className="absolute bottom-0 right-0 h-16 rotate-[-8deg] opacity-90 z-10 translate-y-2 scale-110" alt="Sig" />
                            <div className="absolute bottom-0 right-0 w-full h-[2px] bg-slate-200"></div>
                          </div>
                          <p className="text-[10px] font-black text-slate-900 uppercase tracking-tighter mt-1">IINSAF Authority</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card Back */}
                  <div className={`scale-[0.55] sm:scale-[0.65] md:scale-[0.70] xl:scale-[0.75] origin-top h-[430px] sm:h-[510px] md:h-[550px] xl:h-[580px] overflow-visible transition-all duration-500 ${!isFlipped ? 'hidden xl:block' : 'block xl:block'}`}>
                    <div id="card-back" className="w-[400px] h-[770px] bg-white flex shadow-2xl rounded-2xl overflow-hidden border border-slate-100">
                      <div className="bg-red-600 w-[55px] flex items-center justify-center text-black font-bold relative pb-10">
                        <span className="rotate-90 whitespace-nowrap text-[36px] font-black tracking-[1.2em] uppercase">iinsaf</span>
                      </div>
                      <div className="bg-slate-50 p-4 relative flex-1 flex flex-col h-full">
                        <div className="bg-white rounded-2xl p-6 flex-1 border border-white shadow-inner flex flex-col">
                          <div className="text-center mb-8 pt-2">
                            <img src="/CompanyImages/insaaf_logo-1.png" className="h-24 mx-auto mb-6 object-contain" alt="Logo" />
                            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Official Channels</h4>
                            <div className="bg-white p-3 rounded-2xl border border-slate-100 shadow-md flex items-center gap-4 w-full text-left">
                              <div className="w-14 h-14 bg-white rounded-xl overflow-hidden border-2 border-slate-50 shrink-0 shadow-lg">
                                <img src={card.data.channelLogo || "/images/placeholder.png"} className="w-full h-full object-cover" />
                              </div>
                              <div className="overflow-hidden">
                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-tighter">Primary Channel</p>
                                <p className="text-sm font-black text-slate-900 truncate uppercase">{card.data.channelName}</p>
                              </div>
                            </div>
                          </div>
                          <div className="space-y-6 px-3">
                            {[
                              { label: 'Aadhar No', value: card.data.aadharNo },
                              { label: 'Residential Address', value: card.data.ResidentialAddress },
                              { label: 'Origin Location', value: `${card.data.city}, ${card.data.state}` }
                            ].map((item, i) => (
                              <div key={i} className="space-y-1.5 border-b border-slate-50 pb-2">
                                <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest">{item.label}</span>
                                <p className="text-sm font-bold text-slate-900 uppercase leading-tight">{item.value}</p>
                              </div>
                            ))}
                          </div>
                          <div className="mt-auto pt-6 text-center pb-10">
                            <div className="inline-block p-4 bg-white rounded-3xl shadow-xl border border-slate-50 mb-3 transition-transform hover:scale-105 active:scale-95 duration-500">
                              {qrDataUrl ? <img src={qrDataUrl} width={100} height={100} alt="QR" style={{ imageRendering: 'pixelated' }} /> : <div className="w-[100px] h-[100px] flex items-center justify-center text-[8px] font-black text-slate-300 uppercase">Generating ID...</div>}
                            </div>
                            <p className="text-xs font-black text-slate-900 tracking-[0.2em] uppercase">www.iinsaf.com</p>
                            <p className="text-[10px] text-slate-900 font-bold uppercase">(RNI:HRBIL/25/A2063)</p>
                            <p className="text-[9px] font-bold text-slate-400 mt-1 uppercase tracking-tighter italic">Digitally Verified Identity</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* New Bottom Side Section to match Reporter side */}
          <div className="bg-white/95 backdrop-blur-sm border-t border-gray-100 mt-auto pb-24 sm:pb-10">
            <div className="max-w-7xl mx-auto px-4 py-10">
              <div className="flex flex-col items-center text-center gap-5">
                <img src="/images/iinsaf.png" alt="IINSAF Logo" className="h-12" />
                <div className="space-y-2">
                  <p className="text-[10px] sm:text-xs text-slate-500 font-black max-w-md uppercase tracking-[0.15em] leading-relaxed">
                    International Influencing News Social Media Advertisement Federation
                  </p>
                  <div className="flex items-center justify-center gap-4">
                    <div className="h-[1px] w-8 bg-slate-200"></div>
                    <span className="text-slate-400 text-[10px] font-black uppercase tracking-[0.2em]">Verified Influencer Authority</span>
                    <div className="h-[1px] w-8 bg-slate-200"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default InfluenerGetIdCard;
